import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import AccountSelector from '../../components/AccountSelector';

/**
 * Shared submission form for all Sales Request buckets.
 *
 * Phase 1 ships with a hardcoded field set:
 *   - Submission Type     (locked — taken from route param)
 *   - Submission Reason   (dropdown from request_reasons for this type)
 *   - Property            (AccountSelector — required, search only)
 *   - Vehicle Information (required)
 *   - Notes               (required)
 *   - Photos              (>= 1 required)
 *   - Emergency toggle    (if has_emergency)
 *   - Amount              (if has_amount — refund only)
 *
 * Phase 2 will make these fields configurable per bucket.
 */
export default function RequestFormPage() {
  const { type } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromRequestId = searchParams.get('from');

  const [bucket, setBucket] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sourceRequest, setSourceRequest] = useState(null); // when ?from=N

  // Form state
  const [property, setProperty] = useState(null);
  const [reasonId, setReasonId] = useState('');
  const [vehicleInfo, setVehicleInfo] = useState('');
  const [notes, setNotes] = useState('');
  const [isEmergency, setIsEmergency] = useState(false);
  const [amount, setAmount] = useState('');
  const [refundCategory, setRefundCategory] = useState('');   // 'full' | 'half' | 'other'
  const [badTow, setBadTow] = useState(null);                 // null | true | false (required — forces a choice)
  const [badTowReason, setBadTowReason] = useState('');
  const [photos, setPhotos] = useState([]);
  // Striping Bid fields — only used when bucket.key === 'striping_bid'.
  const [workRequestType, setWorkRequestType]   = useState('');  // 'striping' | 'pressure_washing' | 'both'
  const [propertyOther, setPropertyOther]       = useState(false); // toggle for "Other" prospect mode
  const [propertyOtherName, setPropertyOtherName]       = useState('');
  const [propertyOtherAddress, setPropertyOtherAddress] = useState('');
  const [requestorName, setRequestorName]       = useState('');
  const [requestorEmail, setRequestorEmail]     = useState('');
  const [scopeOfWork, setScopeOfWork]           = useState('');
  const [requestedTimeline, setRequestedTimeline] = useState(''); // 'emergency' | 'standard'
  const [mapFile, setMapFile]                   = useState(null);
  // Sponsored Event fields — reuses property*, requestorName, scopeOfWork above;
  // adds a dedicated date picker.
  const [requestedDate, setRequestedDate]       = useState('');   // YYYY-MM-DD
  // Item Request fields — how many items the rep needs, and what the item is
  // when they pick the "Other" item type.
  const [itemCount, setItemCount]               = useState('');
  const [itemTypeOther, setItemTypeOther]       = useState('');
  // Refund-only submission fields. Sales captures these so GM doesn't have
  // to chase the submitter for payer info when cutting a check.
  const [payerName, setPayerName] = useState('');
  const [payerPhone, setPayerPhone] = useState('');
  const [payerEmail, setPayerEmail] = useState('');
  const [originalPaymentMethod, setOriginalPaymentMethod] = useState('');
  const [releaseDocInfo, setReleaseDocInfo] = useState('');
  const [checkMailingAddress, setCheckMailingAddress] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const types = await api.getRequestTypes();
        const found = (types || []).find(t => t.key === type);
        if (!found) {
          setError(`Unknown request type: ${type}`);
        } else if (found.gm_only) {
          // gm_only buckets cannot be submitted directly — bounce back
          setError(`${found.label} submissions are created automatically when a request is flagged. You cannot submit one directly.`);
          setTimeout(() => navigate(`/sales/requests/${type}`, { replace: true }), 0);
        } else {
          setBucket(found);
          setReasons(found.reasons || []);

          // Pre-fill from a source request if ?from=N is in the URL. Used for
          // the "Already Released → Request a Refund" hand-off on No Charge.
          if (fromRequestId) {
            try {
              const src = await api.getRequest(Number(fromRequestId));
              if (src) {
                setSourceRequest(src);
                if (src.property_id) {
                  setProperty({ id: src.property_id, name: src.account_name || '' });
                }
                if (src.vehicle_info) setVehicleInfo(src.vehicle_info);
                if (src.notes) setNotes(src.notes);
                // For bad-tow we inherit the reason. For No Charge → Refund
                // we auto-note the origin so the GM reviewing the refund has
                // context; sales rep can edit.
                if (src.type === 'no_charge') {
                  setNotes(
                    `Originally submitted as No Charge #${src.id} — released already.${src.notes ? `\n\n${src.notes}` : ''}`
                  );
                }
              }
            } catch (err) {
              console.error('Failed to prefill from source request:', err);
            }
          } else {
            // Clear stale state when navigating between buckets within the SPA
            setSourceRequest(null);
          }
        }
      } catch (err) {
        setError(err.message || 'Failed to load form');
      } finally {
        setLoading(false);
      }
    })();
  }, [type, fromRequestId, navigate]);

  function handlePhotoChange(e) {
    const files = Array.from(e.target.files || []);
    setPhotos(prev => [...prev, ...files]);
  }
  function removePhoto(idx) {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
  }

  // Vendor-style buckets share one form layout. Each bucket defines its own
  // work-type button choices (Striping/Pressure Washing vs Valet/Bulk).
  const WORK_TYPE_BUTTONS_BY_BUCKET = {
    striping_bid: [
      { key: 'striping',         label: 'Striping' },
      { key: 'pressure_washing', label: 'Pressure Washing' },
      { key: 'both',             label: 'Both' },
    ],
    valet_trash: [
      { key: 'valet', label: 'Valet Trash' },
      { key: 'bulk',  label: 'Bulk Trash' },
      { key: 'both',  label: 'Both' },
    ],
  };
  const workTypeButtons = WORK_TYPE_BUTTONS_BY_BUCKET[bucket?.key] || [];
  const isCustomFormBucket = workTypeButtons.length > 0;
  // Legacy alias — older code reads this name.
  const isStripingBid = isCustomFormBucket;
  const isSponsoredEvent = bucket?.key === 'sponsored_event';
  const isItemRequest = bucket?.key === 'item_request';
  const isOtherItem = isItemRequest && /^other$/i.test(reasons.find(r => String(r.id) === String(reasonId))?.name || '');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    // Client-side validation — server validates again
    if (isItemRequest) {
      if (!reasonId) return setError('Item Type is required');
      if (isOtherItem && !itemTypeOther.trim()) return setError('Describe the item you need');
      if (!property?.id) return setError('Property is required');
      const qty = Number(itemCount);
      if (!itemCount || !Number.isInteger(qty) || qty <= 0) {
        return setError('Number of Items is required (whole number greater than 0)');
      }
    } else if (isSponsoredEvent) {
      if (propertyOther) {
        if (!propertyOtherName.trim()) return setError('Property name is required');
      } else if (!property?.id) {
        return setError('Select a property (or switch to Other)');
      }
      if (!requestedDate)        return setError('Requested Date is required');
      if (!requestorName.trim()) return setError('Requestor Name is required');
      if (!scopeOfWork.trim())   return setError('Event Details are required');
    } else if (isStripingBid) {
      if (!workRequestType) return setError('Pick a Work Request Type');
      if (propertyOther) {
        if (!propertyOtherName.trim())    return setError('Property name is required');
        if (!propertyOtherAddress.trim()) return setError('Property address is required');
      } else {
        if (!property?.id) return setError('Select a property (or switch to Other)');
      }
      if (!requestorName.trim())  return setError('Requestor Name is required');
      if (!requestorEmail.trim()) return setError('Requestor Email is required');
      if (!scopeOfWork.trim())    return setError('Scope of Work is required');
      if (!requestedTimeline)     return setError('Pick a Requested Timeline');
    } else {
      if (!property?.id) return setError('Property is required');
      if (!reasonId) return setError('Reason is required');
      if (!vehicleInfo.trim()) return setError('Vehicle information is required');
      if (!notes.trim()) return setError('Notes are required');
      if (photos.length === 0) return setError('At least one photo is required');
      if (bucket?.has_amount) {
        if (!refundCategory) return setError('Pick a refund category: Full, Half, or Other');
        if (refundCategory === 'other' && (!amount || Number(amount) <= 0)) {
          return setError('Enter an amount for Other refunds');
        }
      }
      if (badTow === null) return setError('Please answer: was this a bad tow?');
      if (badTow && !badTowReason.trim()) return setError('Describe why this was a bad tow');
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('type', bucket.key);

      if (isItemRequest) {
        fd.append('property_id', property.id);
        fd.append('reason_id', reasonId);
        if (isOtherItem) fd.append('item_type_other', itemTypeOther.trim());
        fd.append('item_count', String(Number(itemCount)));
        if (notes.trim()) fd.append('notes', notes.trim());
      } else if (isSponsoredEvent) {
        if (propertyOther) {
          fd.append('property_name_other', propertyOtherName.trim());
        } else {
          fd.append('property_id', property.id);
        }
        fd.append('requested_date',  requestedDate);
        fd.append('requestor_name',  requestorName.trim());
        fd.append('scope_of_work',   scopeOfWork.trim());
      } else if (isStripingBid) {
        fd.append('work_request_type', workRequestType);
        if (propertyOther) {
          fd.append('property_name_other',    propertyOtherName.trim());
          fd.append('property_address_other', propertyOtherAddress.trim());
        } else {
          fd.append('property_id', property.id);
        }
        fd.append('requestor_name',     requestorName.trim());
        fd.append('requestor_email',    requestorEmail.trim());
        fd.append('scope_of_work',      scopeOfWork.trim());
        fd.append('requested_timeline', requestedTimeline);
        if (mapFile) fd.append('map', mapFile);
      } else {
        fd.append('property_id', property.id);
        fd.append('reason_id', reasonId);
        fd.append('vehicle_info', vehicleInfo.trim());
        fd.append('notes', notes.trim());
        if (bucket.has_emergency) fd.append('is_emergency', isEmergency ? '1' : '0');
        if (bucket.has_amount) {
          fd.append('refund_category', refundCategory);
          if (refundCategory === 'other' && amount) fd.append('amount_requested', amount);
        }
        fd.append('bad_tow', badTow ? '1' : '0');
        if (badTow) fd.append('bad_tow_reason', badTowReason.trim());
        if (sourceRequest?.id) fd.append('from_request_id', String(sourceRequest.id));
        // Refund-only: payer + payment-origin context so GM can look up the
        // transaction and cut the check without another round of messages.
        if (bucket.key === 'refund') {
          if (payerName.trim())             fd.append('payer_name', payerName.trim());
          if (payerPhone.trim())            fd.append('payer_phone', payerPhone.trim());
          if (payerEmail.trim())            fd.append('payer_email', payerEmail.trim());
          if (originalPaymentMethod)        fd.append('original_payment_method', originalPaymentMethod);
          if (releaseDocInfo.trim())        fd.append('release_doc_info', releaseDocInfo.trim());
          if (checkMailingAddress.trim())   fd.append('check_mailing_address', checkMailingAddress.trim());
        }
        for (const f of photos) fd.append('photos', f);
      }

      const created = await api.submitRequest(fd);
      navigate(`/sales/requests/${bucket.key}/${created.id}`);
    } catch (err) {
      setError(err.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <p style={{ padding: 24, color: 'var(--text-muted)' }}>Loading…</p>;
  if (!bucket) return <div className="admin-page"><p className="alert alert-error">{error || 'Not found'}</p></div>;

  return (
    <div className="admin-page" style={{ maxWidth: 720 }}>
      <button onClick={() => navigate(`/sales/requests/${type}`)}
        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, marginBottom: 12, padding: 0 }}>
        ← Back to {bucket.label}
      </button>

      <div className="page-header">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {bucket.icon && <span style={{ fontSize: 24 }}>{bucket.icon}</span>}
          <span>Submit a new {bucket.label}</span>
        </h2>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {sourceRequest && (
        <div style={{
          padding: 12, marginBottom: 16, borderRadius: 8,
          background: '#fef3c7', border: '1px solid #f59e0b40',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 18 }}>↩️</span>
          <div style={{ fontSize: 13, color: '#78350f' }}>
            Continuing from <strong>{sourceRequest.type_label || sourceRequest.type} #{sourceRequest.id}</strong>.
            Property, vehicle, and notes pre-filled — edit as needed.
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Submission Type (locked) */}
        <div>
          <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
            Submission Type
          </label>
          <div style={{
            padding: '10px 14px', borderRadius: 8,
            background: `${bucket.color}12`, color: bucket.color,
            fontWeight: 700, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 8,
          }}>
            {bucket.icon} {bucket.label}
          </div>
        </div>

        {/* ───────────── Item Request form (custom field set) ───────────── */}
        {isItemRequest && (
          <>
            {/* Item Type — reasons dropdown doubles as the item catalog */}
            <div>
              <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
                Item Type <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <select value={reasonId} onChange={e => setReasonId(e.target.value)} className="form-input" style={{ width: '100%', padding: '10px 12px' }}>
                <option value="">Select an item type…</option>
                {reasons.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>

            {/* "Other" item type — the rep has to say what the item is */}
            {isOtherItem && (
              <div>
                <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
                  What item do you need? <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={itemTypeOther}
                  onChange={e => setItemTypeOther(e.target.value)}
                  placeholder="e.g. Pool party snacks"
                  autoFocus
                  style={{ width: '100%', padding: '10px 12px' }}
                />
              </div>
            )}

            {/* Property — search only, no "Other" (same as tow-style buckets) */}
            <AccountSelector
              value={property}
              onChange={setProperty}
              useOther={false}
              onToggleOther={() => { /* no "Other" per spec */ }}
              otherValue=""
              onOtherChange={() => {}}
              label="Property"
              required
            />

            {/* Number of Items */}
            <div>
              <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
                Number of Items <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="number" min="1" step="1"
                className="form-input"
                value={itemCount}
                onChange={e => setItemCount(e.target.value)}
                placeholder="e.g. 25"
                style={{ width: 200, padding: '10px 12px' }}
              />
            </div>

            {/* Notes — optional */}
            <div>
              <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
                Notes
              </label>
              <textarea
                className="form-input"
                rows={4}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Anything else the reviewer should know (optional)"
                style={{ width: '100%', padding: '10px 12px', resize: 'vertical' }}
              />
            </div>
          </>
        )}

        {/* ───────────── Sponsored Event form (custom field set) ───────────── */}
        {isSponsoredEvent && (
          <>
            {/* Property — same Other-prospect pattern as vendor buckets. */}
            <AccountSelector
              value={property}
              onChange={setProperty}
              useOther={propertyOther}
              onToggleOther={(b) => {
                setPropertyOther(b);
                if (b) setProperty(null);
                else { setPropertyOtherName(''); }
              }}
              otherValue={propertyOtherName}
              onOtherChange={setPropertyOtherName}
              label="Property Name"
              required
            />

            {/* Requested Date */}
            <div>
              <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
                Requested Date <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="date"
                className="form-input"
                value={requestedDate}
                onChange={e => setRequestedDate(e.target.value)}
                style={{ padding: '10px 12px' }}
              />
            </div>

            {/* Requestor Name */}
            <div>
              <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
                Requestor Name <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text" className="form-input"
                value={requestorName}
                onChange={e => setRequestorName(e.target.value)}
                placeholder="Full name"
                style={{ width: '100%', padding: '10px 12px' }}
              />
            </div>

            {/* Event Details */}
            <div>
              <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
                Event Details <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                className="form-input"
                rows={5}
                value={scopeOfWork}
                onChange={e => setScopeOfWork(e.target.value)}
                placeholder="What kind of event? Location, attendance, what Apollo is being asked to sponsor, anything else the reviewer needs…"
                style={{ width: '100%', padding: '10px 12px', resize: 'vertical' }}
              />
            </div>
          </>
        )}

        {/* ───────── Vendor-style buckets (Striping Bid, Valet/Bulk Trash) ───────── */}
        {isCustomFormBucket && (
          <>
            {/* Work Request Type */}
            <div>
              <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
                Work Request Type <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {workTypeButtons.map(opt => {
                  const selected = workRequestType === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setWorkRequestType(opt.key)}
                      style={{
                        flex: '0 0 auto', padding: '10px 24px', fontSize: 14, fontWeight: 700, borderRadius: 8,
                        border: `2px solid ${selected ? bucket.color : 'var(--border-subtle)'}`,
                        background: selected ? `${bucket.color}15` : '#fff',
                        color: selected ? bucket.color : 'var(--text-muted)',
                        cursor: 'pointer', transition: 'all 0.15s ease',
                      }}
                    >{opt.label}</button>
                  );
                })}
              </div>
            </div>

            {/* Property — search existing customers or "Other" prospect.
                Reuses the shared AccountSelector's useOther toggle so the
                typeahead pattern matches the space-calls flow. */}
            <AccountSelector
              value={property}
              onChange={setProperty}
              useOther={propertyOther}
              onToggleOther={(b) => {
                setPropertyOther(b);
                if (b) setProperty(null);
                else { setPropertyOtherName(''); setPropertyOtherAddress(''); }
              }}
              otherValue={propertyOtherName}
              onOtherChange={setPropertyOtherName}
              label="Property"
              required
            />
            {propertyOther && (
              <div>
                <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
                  Property Address <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text" className="form-input"
                  value={propertyOtherAddress}
                  onChange={e => setPropertyOtherAddress(e.target.value)}
                  placeholder="Street, city, state, ZIP"
                  style={{ width: '100%', padding: '10px 12px' }}
                />
              </div>
            )}

            {/* Requestor Name + Email */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div>
                <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
                  Requestor Name <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text" className="form-input"
                  value={requestorName}
                  onChange={e => setRequestorName(e.target.value)}
                  placeholder="Full name"
                  style={{ width: '100%', padding: '10px 12px' }}
                />
              </div>
              <div>
                <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
                  Requestor Email <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="email" className="form-input"
                  value={requestorEmail}
                  onChange={e => setRequestorEmail(e.target.value)}
                  placeholder="name@company.com"
                  style={{ width: '100%', padding: '10px 12px' }}
                />
              </div>
            </div>

            {/* Scope of Work */}
            <div>
              <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
                Details about Scope of Work <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                className="form-input"
                rows={5}
                value={scopeOfWork}
                onChange={e => setScopeOfWork(e.target.value)}
                placeholder="Parking lot, fire lanes, handicap spots, curb paint, square footage, anything else the vendor needs to know…"
                style={{ width: '100%', padding: '10px 12px', resize: 'vertical' }}
              />
            </div>

            {/* Requested Timeline */}
            <div>
              <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
                Requested Timeline <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { key: 'emergency', label: '🚨 Emergency', activeBg: '#ef444415', activeFg: '#ef4444' },
                  { key: 'standard',  label: 'Standard',     activeBg: `${bucket.color}15`, activeFg: bucket.color },
                ].map(opt => {
                  const selected = requestedTimeline === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setRequestedTimeline(opt.key)}
                      style={{
                        flex: '0 0 auto', padding: '10px 28px', fontSize: 14, fontWeight: 700, borderRadius: 8,
                        border: `2px solid ${selected ? opt.activeFg : 'var(--border-subtle)'}`,
                        background: selected ? opt.activeBg : '#fff',
                        color: selected ? opt.activeFg : 'var(--text-muted)',
                        cursor: 'pointer', transition: 'all 0.15s ease',
                      }}
                    >{opt.label}</button>
                  );
                })}
              </div>
            </div>

            {/* Optional Map */}
            <div>
              <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
                Map <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>(optional)</span>
              </label>
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={e => setMapFile(e.target.files?.[0] || null)}
                style={{ fontSize: 13 }}
              />
              {mapFile && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                  Selected: <strong>{mapFile.name}</strong>{' '}
                  <button type="button" onClick={() => setMapFile(null)}
                    style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 0, marginLeft: 8 }}>
                    Remove
                  </button>
                </div>
              )}
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Lot map, sketch, or vendor scope reference (image or PDF).
              </div>
            </div>
          </>
        )}

        {/* ───────────── Tow-style buckets (Refund, No Charge, etc.) ───────────── */}
        {!isStripingBid && !isSponsoredEvent && !isItemRequest && (<>
        {/* Reason */}
        <div>
          <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
            Submission Reason <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <select value={reasonId} onChange={e => setReasonId(e.target.value)} className="form-input" style={{ width: '100%', padding: '10px 12px' }}>
            <option value="">Select a reason…</option>
            {reasons.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>

        {/* Property */}
        <AccountSelector
          value={property}
          onChange={setProperty}
          useOther={false}
          onToggleOther={() => { /* no "Other" per spec */ }}
          otherValue=""
          onOtherChange={() => {}}
          label="Property"
          required
        />

        {/* Vehicle Information */}
        <div>
          <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
            Vehicle Information <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="text"
            className="form-input"
            placeholder="e.g. 2018 Silver Ford F-150 / plate ABC1234"
            value={vehicleInfo}
            onChange={e => setVehicleInfo(e.target.value)}
            style={{ width: '100%', padding: '10px 12px' }}
          />
        </div>

        {/* Amount category buttons — driven by bucket.amount_options.
            Refund: Full / Half / Other. Discount: Half Off / Other. */}
        {bucket.has_amount && (() => {
          const DEFAULT_OPTS = [
            { key: 'full',  label: 'Full' },
            { key: 'half',  label: 'Half' },
            { key: 'other', label: 'Other' },
          ];
          let options = DEFAULT_OPTS;
          try {
            if (bucket.amount_options) {
              const parsed = typeof bucket.amount_options === 'string'
                ? JSON.parse(bucket.amount_options)
                : bucket.amount_options;
              if (Array.isArray(parsed) && parsed.length > 0) options = parsed;
            }
          } catch { /* fallback to default */ }
          return (
          <div>
            <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
              {bucket.label} Amount <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {options.map(opt => {
                const selected = refundCategory === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setRefundCategory(opt.key)}
                    style={{
                      flex: '0 0 auto',
                      padding: '10px 24px',
                      fontSize: 14,
                      fontWeight: 700,
                      borderRadius: 8,
                      border: `2px solid ${selected ? bucket.color : 'var(--border-subtle)'}`,
                      background: selected ? `${bucket.color}15` : '#fff',
                      color: selected ? bucket.color : 'var(--text-muted)',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {refundCategory === 'other' && (
              <div style={{ marginTop: 10 }}>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
                  Enter amount ($) <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="number" step="0.01" min="0"
                  className="form-input"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="e.g. 75.00"
                  style={{ width: 200, padding: '10px 12px' }}
                />
              </div>
            )}
          </div>
          );
        })()}

        {/* Notes */}
        <div>
          <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
            Notes <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <textarea
            className="form-input"
            rows={4}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Provide as much context as possible"
            style={{ width: '100%', padding: '10px 12px', resize: 'vertical' }}
          />
        </div>

        {/* Refund-only: payer + payment-origin info (optional but strongly
            encouraged — the GM uses these to look up the original transaction
            and cut a check to the right person at the right address). */}
        {bucket.key === 'refund' && (
          <div style={{
            padding: 16, border: '1px solid var(--border-subtle)', borderRadius: 10,
            background: '#fafafa', display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
              💳 Payer Information
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400, marginLeft: 8 }}>
                Helps the GM look up the original transaction
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  Payer Name
                </label>
                <input type="text" className="form-input" value={payerName}
                  onChange={e => setPayerName(e.target.value)}
                  placeholder="Who paid for the tow"
                  style={{ width: '100%', padding: '8px 10px' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  Payer Phone
                </label>
                <input type="tel" className="form-input" value={payerPhone}
                  onChange={e => setPayerPhone(e.target.value)}
                  placeholder="(817) 555-1234"
                  style={{ width: '100%', padding: '8px 10px' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  Payer Email
                </label>
                <input type="email" className="form-input" value={payerEmail}
                  onChange={e => setPayerEmail(e.target.value)}
                  placeholder="payer@example.com"
                  style={{ width: '100%', padding: '8px 10px' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  Original Payment Method
                </label>
                <select className="form-input" value={originalPaymentMethod}
                  onChange={e => setOriginalPaymentMethod(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px' }}>
                  <option value="">Select…</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="debit_card">Debit Card</option>
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  Release Doc / Receipt #
                </label>
                <input type="text" className="form-input" value={releaseDocInfo}
                  onChange={e => setReleaseDocInfo(e.target.value)}
                  placeholder="Release or receipt reference"
                  style={{ width: '100%', padding: '8px 10px' }} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  Check Mailing Address
                </label>
                <input type="text" className="form-input" value={checkMailingAddress}
                  onChange={e => setCheckMailingAddress(e.target.value)}
                  placeholder="Where the physical check should be mailed (if applicable)"
                  style={{ width: '100%', padding: '8px 10px' }} />
              </div>
            </div>
          </div>
        )}

        {/* Photos */}
        <div>
          <label style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 }}>
            Photos <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input type="file" accept="image/*" multiple onChange={handlePhotoChange}
            style={{ fontSize: 13 }} />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            At least one required. Up to 10 photos.
          </div>
          {photos.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
              {photos.map((f, i) => (
                <div key={i} style={{
                  position: 'relative', width: 90, height: 90, borderRadius: 8,
                  overflow: 'hidden', border: '1px solid var(--border-subtle)', background: '#f3f4f6',
                }}>
                  <img src={URL.createObjectURL(f)} alt={f.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button type="button" onClick={() => removePhoto(i)}
                    style={{
                      position: 'absolute', top: 2, right: 2, width: 22, height: 22, borderRadius: '50%',
                      background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', fontSize: 14, cursor: 'pointer',
                      lineHeight: '20px', padding: 0,
                    }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Emergency toggle */}
        {bucket.has_emergency && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: 12, border: `1px solid ${isEmergency ? '#ef4444' : 'var(--border-subtle)'}`, borderRadius: 8, background: isEmergency ? '#fef2f2' : 'transparent' }}>
            <input type="checkbox" checked={isEmergency} onChange={e => setIsEmergency(e.target.checked)}
              style={{ width: 18, height: 18, accentColor: '#ef4444' }} />
            <span style={{ fontWeight: 700, fontSize: 14, color: isEmergency ? '#ef4444' : 'var(--text-primary)' }}>
              🚨 Mark as Emergency
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>
              Priority routing & elevated notification
            </span>
          </label>
        )}

        {/* Bad Tow Yes/No — on every submittable bucket */}
        <div style={{
          padding: 14, border: `2px solid ${badTow === true ? '#dc2626' : 'var(--border-subtle)'}`,
          borderRadius: 8, background: badTow === true ? '#fff1f2' : 'transparent',
        }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: 'var(--text-primary)' }}>
            ⚠️ Was this a bad tow? <span style={{ color: '#ef4444' }}>*</span>
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            {[
              { key: false, label: 'No',  activeBg: '#10b98115', activeFg: '#10b981' },
              { key: true,  label: 'Yes', activeBg: '#dc262615', activeFg: '#dc2626' },
            ].map(opt => {
              const selected = badTow === opt.key;
              return (
                <button
                  key={String(opt.key)}
                  type="button"
                  onClick={() => { setBadTow(opt.key); if (!opt.key) setBadTowReason(''); }}
                  style={{
                    flex: '0 0 auto', padding: '10px 28px', fontSize: 14, fontWeight: 700,
                    borderRadius: 8,
                    border: `2px solid ${selected ? opt.activeFg : 'var(--border-subtle)'}`,
                    background: selected ? opt.activeBg : '#fff',
                    color: selected ? opt.activeFg : 'var(--text-muted)',
                    cursor: 'pointer', transition: 'all 0.15s ease',
                  }}
                >{opt.label}</button>
              );
            })}
          </div>
          {badTow === true && (
            <div>
              <label style={{ fontSize: 12, color: '#dc2626', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                Describe why this was a bad tow <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                className="form-input"
                rows={3}
                value={badTowReason}
                onChange={e => setBadTowReason(e.target.value)}
                placeholder="What happened? Who was the driver? Any other context the GM needs…"
                style={{ width: '100%', padding: '10px 12px', resize: 'vertical', borderColor: '#dc262660' }}
              />
              <div style={{ fontSize: 11, color: '#dc2626', marginTop: 6 }}>
                ⚠️ This flag opens a separate GM investigation. The driver will be reviewed and may be held responsible.
              </div>
            </div>
          )}
        </div>
        </>)}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 8 }}>
          <button type="button" className="btn btn-muted" onClick={() => navigate(`/sales/requests/${type}`)} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="btn btn-gold" disabled={submitting} style={{ fontWeight: 700 }}>
            {submitting ? 'Submitting…' : `Submit ${bucket.label}`}
          </button>
        </div>
      </form>
    </div>
  );
}
